<!-- My Media Accreditation Pass Form -->
<template>
    <div class="pa-4">
        <!--Information Text-->
        <app-text size="small">
            <template v-if="isOrganisationUser">
                Click save to submit your pass request. Once submitted, your status will show as Pending until it has been Approved, Rejected, or
                you've been placed on the Reserve List.
            </template>
            <template v-else>
                Please select the users you require passes for. Once you have applied, their status will show as Pending until it has been Approved,
                Rejected, or they've been placed on the Reserve List.
            </template>
            <br />
            The WRU Press Team will contact you by email to confirm your application.
            <br />
            <span class="grey9--text">Please note: If there is a high demand, we may bring the deadline forward.</span>
        </app-text>

        <!--Selected User Section-->
        <template v-if="showSelectedUserSection && !isOrganisationUser">
            <form-section-title class="mt-8" title="Selected User" />
            <div class="d-flex align-center my-4">
                <!--Checkbox-->
                <div
                    @click="handleUserSelection(selectedUser)"
                    class="appWhite d-flex align-center justify-center rounded-lg mr-4"
                    style="aspect-ratio: 1/1; height: 56px; width: 56px"
                >
                    <app-icon color="green" :icon="selectedUsers.includes(selectedUser.userData.entityId) ? 'check' : ''" size="32" />
                </div>

                <avatar class="mr-4" :file="selectedUser.fileData" :size="56" :user="selectedUser.userData" />

                <!--Name | Position-->
                <div>
                    <app-text size="normal-bold">{{ selectedUser.userData.userName }}</app-text>
                    <app-text color="grey9" size="small">{{ selectedUser.userData.userPosition }}</app-text>
                </div>

                <v-spacer />

                <!--Pass Status-->
                <div class="d-flex flex-column align-center">
                    <app-icon :color="getStatusConfig(selectedUser).colour" :icon="getStatusConfig(selectedUser).icon" size="32" />
                    <app-text size="small">{{ selectedUser.passData.accreditationPassStatus }}</app-text>
                </div>
            </div>
            <div class="d-flex align-center">
                <app-form-field
                    form-type="select"
                    class="pr-2"
                    :error="userErrors[selectedUser.userData.entityId]?.eventHolderType"
                    :error-message="userErrors[selectedUser.userData.entityId]?.eventHolderTypeErrorMessage"
                    :items="holderTypeOptions"
                    label="Rights Holder Type"
                    v-model="userHolderTypes[selectedUser.userData.entityId]"
                    style="width: 50%"
                />

                <app-form-field
                    form-type="select"
                    class="pl-2"
                    :error="userErrors[selectedUser.userData.entityId]?.organisation"
                    :error-message="userErrors[selectedUser.userData.entityId]?.organisationErrorMessage"
                    :items="getAvailableOrganisations(selectedUser.userData)"
                    label="Organisation"
                    v-model="userOrganisations[selectedUser.userData.entityId]"
                    style="width: 50%"
                />
            </div>
        </template>

        <!--Users Applied-->
        <template v-if="!isOrganisationUser">
            <form-section-title class="mt-8" description="Other Users from your Organisation that have applied for passes." title="Users Applied" />
            <app-text v-if="!computedUsersApplied.length" class="text-center" color="grey9"> There are no other Users to show. </app-text>
            <div v-for="(item, index) in computedUsersApplied" :key="`applied-user-${item.userData.entityId}`">
                <div class="d-flex align-center mt-4">
                    <!--Checkbox-->
                    <div
                        @click="handleUserSelection(item)"
                        class="appWhite d-flex align-center justify-center rounded-lg mr-4"
                        style="aspect-ratio: 1/1; height: 56px; width: 56px"
                    >
                        <app-icon color="green" :icon="selectedUsers.includes(item.userData.entityId) ? 'check' : ''" size="32" />
                    </div>

                    <avatar class="mr-4" :file="item.fileData" :size="56" :user="item.userData" />

                    <!--Name | Position-->
                    <div>
                        <app-text size="normal-bold">{{ item.userData.userName }}</app-text>
                        <app-text color="grey9" size="small">{{ item.userData.userPosition }}</app-text>
                    </div>

                    <v-spacer />

                    <!--Pass Status-->
                    <div class="d-flex flex-column align-center">
                        <app-icon :color="getStatusConfig(item).colour" :icon="getStatusConfig(item).icon" size="32" />
                        <app-text size="small">{{ item.passData.accreditationPassStatus }}</app-text>
                    </div>
                </div>

                <!--Rights Holder Type and Organisation - Now below user info-->
                <div v-if="selectedUsers.includes(item.userData.entityId)" class="mt-4 d-flex align-center">
                    <app-form-field
                        form-type="select"
                        :error="userErrors[item.userData.entityId]?.eventHolderType"
                        :error-message="userErrors[item.userData.entityId]?.eventHolderTypeErrorMessage"
                        :items="holderTypeOptions"
                        label="Rights Holder Type"
                        v-model="userHolderTypes[item.userData.entityId]"
                        style="width: 50%"
                    />

                    <app-form-field
                        form-type="select"
                        class="ml-4"
                        :error="userErrors[item.userData.entityId]?.organisation"
                        :error-message="userErrors[item.userData.entityId]?.organisationErrorMessage"
                        :items="getAvailableOrganisations(item.userData)"
                        label="Organisation"
                        v-model="userOrganisations[item.userData.entityId]"
                        style="width: 50%"
                    />
                </div>

                <v-divider class="greyD mt-4" />
            </div>

            <!--Users Not Applied-->
            <form-section-title
                class="mt-8"
                description="Other Users from your Organisation that have NOT applied for passes."
                title="Users Not Yet Applied"
            />
            <app-text v-if="!computedUsersNotApplied.length" class="text-center" color="grey9"> There are no other Users to show. </app-text>
            <div v-for="(item, index) in computedUsersNotApplied" :key="`not-applied-user-${item.userData.entityId}`">
                <div class="d-flex align-center mt-4">
                    <!--Checkbox-->
                    <div
                        @click="handleUserSelection(item)"
                        class="appWhite d-flex align-center justify-center rounded-lg mr-4"
                        style="aspect-ratio: 1/1; height: 56px; width: 56px"
                    >
                        <app-icon color="green" :icon="selectedUsers.includes(item.userData.entityId) ? 'check' : ''" size="32" />
                    </div>

                    <avatar class="mr-4" :file="item.fileData" :size="56" :user="item.userData" />

                    <!--Name | Position-->
                    <div>
                        <app-text size="normal-bold">{{ item.userData.userName }}</app-text>
                        <app-text color="grey9" size="small">{{ item.userData.userPosition }}</app-text>
                    </div>
                </div>

                <!--Rights Holder Type and Organisation - Now showing both fields-->
                <div v-if="selectedUsers.includes(item.userData.entityId)" class="mt-4 d-flex align-center">
                    <app-form-field
                        form-type="select"
                        :error="userErrors[item.userData.entityId]?.eventHolderType"
                        :error-message="userErrors[item.userData.entityId]?.eventHolderTypeErrorMessage"
                        :items="holderTypeOptions"
                        label="Rights Holder Type"
                        v-model="userHolderTypes[item.userData.entityId]"
                        style="width: 50%"
                    />

                    <app-form-field
                        form-type="select"
                        class="ml-4"
                        :error="userErrors[item.userData.entityId]?.organisation"
                        :error-message="userErrors[item.userData.entityId]?.organisationErrorMessage"
                        :items="getAvailableOrganisations(item.userData)"
                        label="Organisation"
                        v-model="userOrganisations[item.userData.entityId]"
                        style="width: 50%"
                    />
                </div>

                <v-divider class="greyD mt-4" />
            </div>
        </template>
        <!--Organisation User View-->
        <template v-if="isOrganisationUser && currentUserData">
            <form-section-title class="mt-8" title="Your Pass Request" />

            <div class="d-flex align-center mt-4">
                <avatar class="mr-4" :file="currentUserData.fileData" :size="56" :user="currentUserData.userData" />

                <!--Name | Position-->
                <div v-if="currentUserData.userData">
                    <app-text size="normal-bold">{{ currentUserData.userData.userName }}</app-text>
                    <app-text color="grey9" size="small">{{ currentUserData.userData.userPosition }}</app-text>
                </div>

                <v-spacer />

                <!--Pass Status-->
                <div class="d-flex flex-column align-center" v-if="currentUserData.passData">
                    <app-icon :color="getStatusConfig(currentUserData).colour" :icon="getStatusConfig(currentUserData).icon" size="32" />
                    <app-text size="small">{{ currentUserData.passData.accreditationPassStatus }}</app-text>
                </div>
            </div>

            <!--Rights Holder Type-->
            <app-form-field
                v-if="showRightsHolderType"
                form-type="select"
                class="my-4"
                :error="errors.eventHolderType"
                :error-message="errors.eventHolderTypeErrorMessage"
                :items="holderTypeOptions"
                label="Rights Holder Type"
                v-model="eventForm.holderType"
            />

            <!--Organisation Selection-->
            <app-form-field
                v-if="showRightsHolderType && !hasAlreadyApplied"
                form-type="autoComplete"
                class="mt-4"
                :disabled="hasAlreadyApplied"
                :error="errors.organisations"
                :error-message="errors.organisationsErrorMessage"
                :items="computedOrganisationOptions"
                label="Select Organisation"
                :multiple="false"
                v-model="selectedOrganisations"
            />
        </template>

        <!--Save Button-->
        <div class="d-flex justify-end mt-4">
            <app-btn
                v-if="showSaveButton"
                @click.native="savePasses"
                color="green"
                :disabled="computedIsButtonDisabled"
                icon="save"
                :label="isOrganisationUser ? 'Submit Request' : 'Save'"
            />
        </div>

        <!--Dialogs-->
        <v-dialog max-width="512" v-model="isRemovedUsersDialogVisible">
            <div class="appGrey rounded-lg pa-4">
                <app-text>You are about to delete the passes for the following people:</app-text>

                <app-text v-for="(item, index) in removedUserNames" :key="`removed-${index}`" class="mt-4 ml-4" size="normal-bold"
                    >- {{ item }}</app-text
                >

                <app-text class="mt-4" size="normal-bold">Are you sure you want to delete these passes?</app-text>

                <div class="d-flex justify-space-between mt-4">
                    <!--No-->
                    <app-btn @click.native="isRemovedUsersDialogVisible = false" color="grey" icon="cancel" label="No" />

                    <!--Yes-->
                    <app-btn @click.native="confirmDeletePasses" color="red" icon="delete" label="Yes" />
                </div>
            </div>
        </v-dialog>
    </div>
</template>

<script>
export default {
    name: 'MyMediaAccreditationPassForm',

    props: ['accreditationData', 'selectedUser'],

    data: () => ({
        isRemovedUsersDialogVisible: false,
        removedUserNames: [],
        selectedUsers: [],
        selectedOrganisations: [],
        userHolderTypes: {},
        userErrors: {},
        holderTypeOptions: ['Rights Holder', 'Non-Rights Holder'],
        userOrganisations: {},
        errors: {
            eventHolderType: false,
            eventHolderTypeErrorMessage: ''
        },
        eventForm: {
            holderType: ''
        }
    }),

    computed: {
        isOrganisationUser() {
            return this.MIX_getCurrentUser().userLevel === 'Organisation-User'
        },

        // This checks if the user is a manager or admin
        isManagerOrAdmin() {
            const userLevel = this.MIX_getCurrentUser().userLevel
            return userLevel === 'Organisation-Manager' || userLevel === 'Staff-Admin'
        },

        currentUserData() {
            const currentUser = this.MIX_getCurrentUser()
            return (
                this.accreditationData.passesData.find((item) => item.userData.entityId === currentUser.entityId) ||
                this.accreditationData.organisationUsersData.find((item) => item.userData.entityId === currentUser.entityId)
            )
        },
        /**
         * Show Selected User Section
         *
         * Only show if a user was specifically selected and not auto-selected through "Apply for Passes"
         *
         * @returns {boolean}
         */
        showSelectedUserSection() {
            const t = this
            return t.selectedUser?.userData?.entityId && t.selectedUser?.isSpecificallySelected
        },
        /**
         * Computed Is Button Disabled
         *
         * Return true if the selectedUsers array is the same as the original, false otherwise.
         *
         * @returns {boolean}
         */
        computedIsButtonDisabled() {
            const t = this

            // If no users are selected at all, disable the button
            // if (!t.selectedUsers.length) return true

            // Only check if selected users have required fields
            const hasRequiredFields = t.selectedUsers.every((userId) => t.userHolderTypes[userId] && t.userOrganisations[userId])

            return !hasRequiredFields
        },

        /**
         * Computed Users Applied
         *
         * Return the users that have applied for the accreditation pass.
         * For Organisation-Users, only return themselves.
         * For other users, return all users except the selected user.
         *
         * @returns {array} - An array of objects containing the pass data (fileData{}, passData{}, userData{})
         */
        computedUsersApplied() {
            const t = this
            let tableData = (t.$props.accreditationData.passesData || [])
                .filter((item) => ['Approved', 'Pending', 'Rejected', 'Reserved'].includes(item.passData.accreditationPassStatus))
                // Remove duplicates by userId
                .filter((item, index, self) => index === self.findIndex((t) => t.userData.entityId === item.userData.entityId))

            const CURRENT_USER = t.MIX_getCurrentUser()

            // If Organisation-User, only show their own data
            if (CURRENT_USER.userLevel === 'Organisation-User') {
                tableData = tableData.filter((item) => item.userData.entityId === CURRENT_USER.entityId)
            } else {
                // For other user levels, remove the selected user from the list
                if (t.selectedUser.userData?.entityId) {
                    tableData = tableData.filter((item) => item.userData.entityId !== t.selectedUser.userData.entityId)
                }
            }

            // Sort by UserName
            return tableData.sort((a, b) => a.userData.userName.localeCompare(b.userData.userName))
        },

        /**
         * Computed Users Not Applied
         *
         * Return the users that have not applied for the accreditation pass.
         * For Organisation-Users, only return themselves if they haven't applied.
         * For other users, return all users except the selected user.
         *
         * @returns {array} - An array of objects containing the Organisation Users data (fileData{}, userData{})
         */
        computedUsersNotApplied() {
            const t = this
            let tableData = (t.$props.accreditationData.passesData || [])
                .filter((item) => item.passData.accreditationPassStatus === 'Not Applied' || !item.passData.accreditationPassStatus)
                // Remove duplicates by userId
                .filter((item, index, self) => index === self.findIndex((t) => t.userData.entityId === item.userData.entityId))

            const CURRENT_USER = t.MIX_getCurrentUser()

            // If Organisation-User, only show their own data
            if (CURRENT_USER.userLevel === 'Organisation-User') {
                tableData = tableData.filter((item) => item.userData.entityId === CURRENT_USER.entityId)
            } else {
                // For other user levels, remove the selected user from the list
                if (t.selectedUser.userData?.entityId) {
                    tableData = tableData.filter((item) => item.userData.entityId !== t.selectedUser.userData.entityId)
                }
            }

            // Sort by UserName
            return tableData.sort((a, b) => a.userData.userName.localeCompare(b.userData.userName))
        },
        /**
         * Get Current User Pass Data
         *
         * Gets only the pass data for the current user
         *
         * @returns {Object|null} - The pass data object or null if not found
         */
        currentUserPassData() {
            const t = this
            // Check if accreditationData and passesData exist
            if (!t.accreditationData?.passesData) return null

            const currentUser = t.MIX_getCurrentUser()
            return t.accreditationData.passesData.find((item) => item.userData.entityId === currentUser.entityId)
        },

        /**
         * Get Current User Data
         *
         * Gets the full user data including profile information
         *
         * @returns {Object} - The user data object
         */
        currentUserData() {
            const t = this
            // Check if accreditationData exists
            if (!t.accreditationData) return null

            const currentUser = t.MIX_getCurrentUser()
            const passData = t.currentUserPassData

            // Check if organisationUsersData exists
            if (!t.accreditationData.organisationUsersData) return passData || null

            const userData = t.accreditationData.organisationUsersData.find((item) => item.userData.entityId === currentUser.entityId)

            return passData || userData || null
        },

        /**
         * Show Save Button
         *
         * Controls the visibility of the save/submit button
         * - For Organisation Users: Only show if they haven't applied or don't have a pass status
         * - For other users: Always show
         *
         * @returns {boolean}
         */
        showSaveButton() {
            const t = this

            // For non-organisation users, always show the button
            if (!t.isOrganisationUser) return true

            // For organisation users, check if they have applied
            const passData = t.currentUserPassData
            return !passData || passData.passData.accreditationPassStatus === 'Not Applied'
        },
        /**
         * Show Rights Holder Type
         *
         * Controls the visibility of the rights holder type field
         * - For Organisation Users: Only show if they haven't applied or don't have a pass status
         * - For other users: Always show
         *
         * @returns {boolean}
         */
        showRightsHolderType() {
            const t = this

            // For non-organisation users, always show the field
            if (!t.isOrganisationUser) return true

            // For organisation users, check if they have applied
            const passData = t.currentUserPassData
            return !passData || passData.passData.accreditationPassStatus === 'Not Applied'
        },

        /**
         * Computed Organisation Options
         *
         * Return the list of available organisations based on user selection
         * in the format required for the app-form-field
         *
         * @returns {Array} Array of organisation options
         */
        computedOrganisationOptions() {
            const t = this
            const selectedUserData = t.isOrganisationUser ? t.currentUserData?.userData : t.selectedUser?.userData

            if (!selectedUserData?.userOrganisation) return []

            const userOrgs = Array.isArray(selectedUserData.userOrganisation)
                ? selectedUserData.userOrganisation
                : [selectedUserData.userOrganisation]

            return userOrgs.map((orgId) => ({
                text: t.$props.accreditationData?.organisations?.[orgId] || orgId,
                value: orgId
            }))
        },

        /**
         * Has Already Applied
         *
         * Check if the user has already applied for a pass
         *
         * @returns {boolean}
         */
        hasAlreadyApplied() {
            const t = this
            if (t.isOrganisationUser) {
                const passData = t.currentUserPassData
                return passData && passData.passData?.accreditationPassStatus !== 'Not Applied'
            }
            return false
        }
    },

    methods: {
        /**
         * Check if Pass Data Changed
         *
         * Compare the existing pass data with new selections to determine if anything changed
         *
         * @param {Object} existingPass - Current pass data for the user
         * @param {String} newHolderType - New holder type selection
         * @param {Array|String} newOrganisations - New organisation selection
         * @returns {Boolean} - True if changes detected, false if no changes
         */
        hasPassDataChanged(existingPass, newHolderType, newOrganisations) {
            if (!existingPass) return true // New pass

            // Convert organisations to arrays for comparison
            const existingOrgs = [existingPass.accreditationPassOrganisationId].flat().filter(Boolean)
            const newOrgs = Array.isArray(newOrganisations) ? newOrganisations : [newOrganisations]

            // Compare holder type and organisations
            const holderTypeChanged = existingPass.accreditationPassHolderType !== newHolderType
            const orgsChanged = JSON.stringify(existingOrgs.sort()) !== JSON.stringify(newOrgs.sort())

            return holderTypeChanged || orgsChanged
        },
        /**
         * Confirm Delete Passes
         *
         * On confirming the removal of passes, emit the savePasses event back to the parent.
         */
        confirmDeletePasses() {
            const t = this
            t.isRemovedUsersDialogVisible = false

            // Update to pass data in the expected format
            t.$emit('emitSavePasses', {
                userIds: t.selectedUsers,
                holderType: '', // Empty for deletions
                organisations: [] // Empty for deletions
            })
        },

        /**
         * Get Status Config
         *
         * Return the colour and icon for the pass status.
         *
         * @param item {Object} - The pass object {fileData{}, passData{}, userData{}
         * @returns {{colour: string, icon: string}}
         */
        getStatusConfig(item) {
            let color
            let icon

            switch (item.passData.accreditationPassStatus) {
                case 'Approved':
                    color = 'green'
                    icon = 'success'
                    break
                case 'Rejected':
                    color = 'red'
                    icon = 'close'
                    break
                case 'Pending':
                    color = 'orange'
                    icon = 'pending'
                    break
                case 'Reserved':
                    color = 'blue'
                    icon = 'reserved'
                    break
                default:
                    color = 'grey9'
                    icon = 'help'
                    break
            }

            return { colour: color, icon: icon }
        },

        /**
         * Handle User Selection
         *
         * Add or remove the selectedUser from the selectedUsers array and initialize their holder type.
         *
         * @param item {Object} - The pass object {fileData{}, passData{}, userData{}}
         */
        handleUserSelection(item) {
            const t = this
            const userId = item.userData.entityId

            // If the user is already selected, remove them and their data
            if (t.selectedUsers.includes(userId)) {
                t.selectedUsers = t.selectedUsers.filter((id) => id !== userId)
                delete t.userHolderTypes[userId]
                delete t.userErrors[userId]
                delete t.userOrganisations[userId]
            } else {
                // Add user and initialize their holder type and organisation
                t.selectedUsers.push(userId)

                // Initialize with existing pass data if available
                if (item.passData) {
                    t.$set(t.userHolderTypes, userId, item.passData.accreditationPassHolderType || '')

                    // Get organisation from pass data
                    const passOrganisation =
                        item.passData.organisations?.[0] ||
                        item.passData.accreditationPassOrganisationId ||
                        (Array.isArray(item.passData.organisations) ? item.passData.organisations[0] : item.passData.organisations)

                    t.$set(t.userOrganisations, userId, passOrganisation || '')
                } else {
                    // Initialize with empty values for new selections
                    t.$set(t.userHolderTypes, userId, '')
                    // Initialize with first available organization if no pass data
                    const availableOrgs = t.getAvailableOrganisations(item.userData)
                    t.$set(t.userOrganisations, userId, availableOrgs[0]?.value || '')
                }

                // Initialize error objects
                t.$set(t.userErrors, userId, {
                    eventHolderType: false,
                    eventHolderTypeErrorMessage: '',
                    organisation: false,
                    organisationErrorMessage: ''
                })
            }
        },

        /**
         * Validate User Forms
         *
         * Validate the holder type for each selected user
         *
         * @returns {boolean} - if all forms have passed validation
         */
        validateUserForms() {
            const t = this
            let isValid = true

            // Clear all previous errors
            t.userErrors = {}

            // Validate each selected user
            t.selectedUsers.forEach((userId) => {
                t.$set(t.userErrors, userId, {
                    eventHolderType: false,
                    eventHolderTypeErrorMessage: '',
                    organisation: false,
                    organisationErrorMessage: ''
                })

                if (!t.userHolderTypes[userId]) {
                    t.userErrors[userId].eventHolderType = true
                    t.userErrors[userId].eventHolderTypeErrorMessage = 'Rights Holder Type is required'
                    isValid = false
                }

                if (!t.userOrganisations[userId]) {
                    t.userErrors[userId].organisation = true
                    t.userErrors[userId].organisationErrorMessage = 'Organisation is required'
                    isValid = false
                }
            })

            return isValid
        },

        /**
         * Emit Close Panel
         *
         * Emit event to parent to close the right panel
         */
        emitClosePanel() {
            this.$emit('closePanel')
        },

        /**
         * Save Passes
         *
         * Enhanced to handle individual holder types for each user
         */
        async savePasses() {
            const t = this

            if (t.isOrganisationUser) {
                // Validate event form data
                if (!t.validateEventForm()) return

                const currentPass = t.currentUserData?.passData

                // Check if anything has actually changed
                // Compare against accreditationPassOrganisationId - a Pass has no `organisations` field, so
                // the old comparison was always unequal and forced a save (and an Organisation overwrite)
                // on every submission (sc-25174)
                const CURRENT_ORGANISATIONS = Array.isArray(currentPass?.accreditationPassOrganisationId)
                    ? [...currentPass.accreditationPassOrganisationId].filter(Boolean).sort()
                    : [currentPass?.accreditationPassOrganisationId].filter(Boolean).sort()

                const SELECTED_ORGANISATIONS = [t.selectedOrganisations].flat().filter(Boolean).sort()

                const hasChanges =
                    !currentPass ||
                    currentPass.accreditationPassHolderType !== t.eventForm.holderType ||
                    JSON.stringify(CURRENT_ORGANISATIONS) !== JSON.stringify(SELECTED_ORGANISATIONS)

                if (!hasChanges) {
                    t.emitClosePanel()
                    return
                }

                // Only emit if there are changes
                t.$emit('emitSavePasses', {
                    userIds: [t.MIX_getCurrentUser().entityId],
                    holderType: t.eventForm.holderType,
                    organisations: SELECTED_ORGANISATIONS
                })
                return
            }

            if (t.computedIsButtonDisabled) return

            // Validate all selected users
            if (!t.validateUserForms()) return

            // Get currently selected users that already have passes
            const existingPassUsers = t.selectedUsers.filter((userId) => {
                const userPassData = t.$props.accreditationData.passesData.find((item) => item.userData.entityId === userId && item.passData)
                return !!userPassData
            })

            // Check if any changes were made to existing passes
            const hasChanges = existingPassUsers.some((userId) => {
                const existingPass = t.$props.accreditationData.passesData.find((item) => item.userData.entityId === userId)?.passData

                if (!existingPass) return false

                // Convert to arrays and sort before comparison
                const existingOrgs = Array.isArray(existingPass.accreditationPassOrganisationId)
                    ? [...existingPass.accreditationPassOrganisationId].sort()
                    : [existingPass.accreditationPassOrganisationId].sort()

                const newOrgs = Array.isArray(t.userOrganisations[userId])
                    ? [...t.userOrganisations[userId]].sort()
                    : [t.userOrganisations[userId]].sort()

                // Compare holder type and organizations
                const holderTypeChanged = existingPass.accreditationPassHolderType !== t.userHolderTypes[userId]
                const organizationChanged = JSON.stringify(existingOrgs) !== JSON.stringify(newOrgs)

                return holderTypeChanged || organizationChanged
            })

            // Get users without passes (new selections)
            const newUsers = t.selectedUsers.filter((userId) => {
                return !t.$props.accreditationData.passesData.find((item) => item.userData.entityId === userId && item.passData)
            })

            // Check for removed users
            const ORIGINAL_SELECTED_USERS = t.$props.accreditationData.passesData
                .filter((item) => ['Approved', 'Pending', 'Rejected', 'Reserved'].includes(item.passData.accreditationPassStatus))
                .map((item) => item.userData.entityId)

            const removedUsers = ORIGINAL_SELECTED_USERS.filter((userId) => !t.selectedUsers.includes(userId))

            // If nothing has changed, just close the panel
            if (!hasChanges && newUsers.length === 0 && removedUsers.length === 0) {
                t.emitClosePanel()
                return
            }

            // If we're only removing users, show the removal dialog
            if (!hasChanges && newUsers.length === 0 && removedUsers.length > 0) {
                t.removedUserNames = t.$props.accreditationData.passesData
                    .filter((item) => removedUsers.includes(item.userData.entityId))
                    .map((item) => item.userData.userName)
                t.isRemovedUsersDialogVisible = true
                return
            }

            // Only proceed with save if there are actual changes
            // Never submit an empty Organisation array. A Pass saved with no Organisation is written to
            // Redis as an empty field, is not indexed, and can no longer be matched by any Organisation
            // lookup - which is what hid applicants from the accreditation list and exports (sc-25174).
            // Fall back to the User's own Organisation when the dropdown could not resolve one.
            const userDataToSubmit = t.selectedUsers.map((userId) => {
                const USER_DATA = t.$props.accreditationData?.passesData?.find((item) => item.userData?.entityId === userId)?.userData

                const ORGANISATIONS = [t.userOrganisations[userId]].flat().filter(Boolean).length
                    ? [t.userOrganisations[userId]].flat().filter(Boolean)
                    : [USER_DATA?.userOrganisation].flat().filter(Boolean)

                return {
                    userId: userId,
                    holderType: t.userHolderTypes[userId] || '',
                    organisations: ORGANISATIONS
                }
            })

            if (userDataToSubmit.length > 0) {
                // Process each user individually to ensure their specific data is sent
                for (const userData of userDataToSubmit) {
                    t.$emit('emitSavePasses', {
                        userIds: [userData.userId], // Send as single user
                        holderType: userData.holderType,
                        organisations: userData.organisations
                    })
                }
            }
        },

        /**
         * Validate Event Form
         *
         * Validate the event-specific form fields
         *
         * @returns {boolean} - if the form has passed validation
         */
        validateEventForm() {
            const t = this
            let isValid = true

            // Clear previous errors
            t.errors.eventHolderType = false
            t.errors.eventHolderTypeErrorMessage = ''
            t.errors.organisations = false
            t.errors.organisationsErrorMessage = ''

            // Validate holder type
            if (!t.eventForm.holderType) {
                t.errors.eventHolderType = true
                t.errors.eventHolderTypeErrorMessage = 'Rights Holder Type is required'
                isValid = false
            }

            // Validate organisations
            if (!t.selectedOrganisations) {
                t.errors.organisations = true
                t.errors.organisationsErrorMessage = 'An organisation must be selected'
                isValid = false
            }

            return isValid
        },
        getAvailableOrganisations(userData) {
            const t = this
            const currentUser = t.MIX_getCurrentUser()

            // Ensure we handle both array and single value cases
            const managerOrgs = Array.isArray(currentUser.userOrganisation) ? currentUser.userOrganisation : [currentUser.userOrganisation]

            const userOrgs = Array.isArray(userData.userOrganisation) ? userData.userOrganisation : [userData.userOrganisation]

            // Get intersection of manager and user orgs
            const availableOrgs = userOrgs.filter((org) => managerOrgs.includes(org))

            // Map to the format needed for the dropdown
            return availableOrgs.map((orgId) => ({
                text: t.$props.accreditationData?.organisations?.[orgId] || orgId,
                value: orgId
            }))
        }
    },

    mounted() {
        const t = this
        if (!t.isOrganisationUser) {
            // Pre-select users and initialize their holder types
            const appliedUsers = t.$props.accreditationData.passesData.filter((item) =>
                ['Approved', 'Pending', 'Rejected', 'Reserved'].includes(item.passData.accreditationPassStatus)
            )

            t.selectedUsers = appliedUsers.map((item) => item.userData.entityId)

            // Initialize holder types and organisations for pre-selected users
            appliedUsers.forEach((item) => {
                const userId = item.userData.entityId

                // Set holder type from pass data
                t.$set(t.userHolderTypes, userId, item.passData.accreditationPassHolderType || '')

                // Set organisation from pass data - Handle both array and single value cases
                const passOrgs = item.passData.accreditationPassOrganisationId
                const orgValue = Array.isArray(passOrgs) ? passOrgs[0] : passOrgs
                t.$set(t.userOrganisations, userId, orgValue || '')

                // Initialize error objects
                t.$set(t.userErrors, userId, {
                    eventHolderType: false,
                    eventHolderTypeErrorMessage: '',
                    organisation: false,
                    organisationErrorMessage: ''
                })
            })

            // If a specific user was selected, pre-select their data too
            if (t.selectedUser?.userData?.entityId) {
                const userId = t.selectedUser.userData.entityId
                if (!t.selectedUsers.includes(userId)) {
                    t.selectedUsers.push(userId)
                }

                // Set holder type and organisation if they have pass data
                if (t.selectedUser.passData) {
                    t.$set(t.userHolderTypes, userId, t.selectedUser.passData.accreditationPassHolderType || '')
                    const passOrgs = t.selectedUser.passData.accreditationPassOrganisationId
                    const orgValue = Array.isArray(passOrgs) ? passOrgs[0] : passOrgs
                    t.$set(t.userOrganisations, userId, orgValue || '')
                } else {
                    // For new users, initialize with their first available organisation
                    const availableOrgs = t.getAvailableOrganisations(t.selectedUser.userData)
                    t.$set(t.userOrganisations, userId, availableOrgs[0]?.value || '')
                }

                // Initialize error objects for the selected user
                t.$set(t.userErrors, userId, {
                    eventHolderType: false,
                    eventHolderTypeErrorMessage: '',
                    organisation: false,
                    organisationErrorMessage: ''
                })
            }
        } else {
            const passData = t.currentUserPassData
            // Pre-fill organisations if user has already applied
            if (t.hasAlreadyApplied) {
                // Get the first organization from possible data sources
                t.selectedOrganisations =
                    passData?.passData?.organisations?.[0] ||
                    passData?.passData?.accreditationPassOrganisationId ||
                    passData?.userData?.userOrganisation ||
                    ''
            } else {
                // For new applications, initialize with user's first organisation
                t.selectedOrganisations = Array.isArray(t.currentUserData?.userData?.userOrganisation)
                    ? t.currentUserData.userData.userOrganisation[0]
                    : t.currentUserData?.userData?.userOrganisation || ''
            }
        }

        // Handle any specifically selected user data
        if (t.selectedUser?.passData) {
            const userId = t.selectedUser.userData.entityId
            t.$set(t.userHolderTypes, userId, t.selectedUser.passData.accreditationPassHolderType || '')

            const passOrgs = t.selectedUser.passData.accreditationPassOrganisationId
            const orgValue = Array.isArray(passOrgs) ? passOrgs[0] : passOrgs
            t.$set(t.userOrganisations, userId, orgValue || '')
        }
    }
}
</script>

<style scoped></style>
